/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { projectsCuisinesData } from "../../CollectionsData";
import NotFound from "../../../../Components/NotFound";
import { getProjectImages } from "../../../../utils/projectImages";
import "./CuisinesProject.css";
import projectImageBottom from "../../../../images/Cuisines/cuisineFooter.jpg";

gsap.registerPlugin(ScrollTrigger);

export default function CuisinesProject() {
  const { name } = useParams();
  const path = decodeURIComponent(name || "");
  const project = projectsCuisinesData[path];

  // Images chargées automatiquement depuis le dossier du projet (voir utils/projectImages.js).
  // La clé gallery du projet est optionnelle : si elle existe, elle impose l'ordre manuel.
  const images = useMemo(
    () => getProjectImages("cuisines", path, project?.gallery),
    [path, project]
  );

  const sectionRef = useRef(null);
  const leftImgRef = useRef(null);
  const rightImgRef = useRef(null);
  const galleryRef = useRef(null);
  const inspirationRef = useRef(null);

  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [relatedCollections, setRelatedCollections] = useState([]);

  // ── 1. ProjectBody fade-in ─────────────────────────────────────────────
  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    const ctx = gsap.context(() => {
      gsap.fromTo(
        section,
        { opacity: 0, y: 50 },
        {
          opacity: 1,
          y: 0,
          duration: 0.9,
          ease: "power2.out",
          scrollTrigger: {
            trigger: section,
            start: "top 85%",
            once: true,
          },
        }
      );
    }, section);

    return () => ctx.revert();
  }, []);

  // ── 2. Parallax sur les deux images du ProjectBody ─────────────────────
  useEffect(() => {
    const section = sectionRef.current;
    const leftImg = leftImgRef.current;
    const rightImg = rightImgRef.current;
    if (!section || !leftImg || !rightImg) return;

    const ctx = gsap.context(() => {
      gsap.to(leftImg, {
        yPercent: -8,
        ease: "none",
        scrollTrigger: {
          trigger: section,
          start: "top bottom",
          end: "bottom top",
          scrub: true,
        },
      });

      gsap.to(rightImg, {
        yPercent: -5,
        ease: "none",
        scrollTrigger: {
          trigger: section,
          start: "top bottom",
          end: "bottom top",
          scrub: true,
        },
      });
    });

    return () => ctx.revert();
  }, []);

  // ── 3. Gallery items stagger ───────────────────────────────────────────
  useEffect(() => {
    const gallery = galleryRef.current;
    if (!gallery) return;

    const ctx = gsap.context(() => {
      const items = gallery.querySelectorAll(".ProjectGallery_item");

      gsap.fromTo(
        items,
        { opacity: 0, y: 40 },
        {
          opacity: 1,
          y: 0,
          duration: 0.7,
          ease: "power2.out",
          stagger: 0.12,
          scrollTrigger: {
            trigger: gallery,
            start: "top 88%",
            once: true,
          },
        }
      );
    }, gallery);

    return () => ctx.revert();
  }, [path]);

  // ── 4. Inspiration fade-in + parallax image ────────────────────────────
  useEffect(() => {
    const section = inspirationRef.current;
    if (!section) return;

    const ctx = gsap.context(() => {
      gsap.fromTo(
        section.querySelector(".inspiration-content"),
        { opacity: 0, x: -40 },
        {
          opacity: 1,
          x: 0,
          duration: 0.9,
          ease: "power2.out",
          scrollTrigger: {
            trigger: section,
            start: "top 80%",
            once: true,
          },
        }
      );

      gsap.to(section.querySelector(".inspiration-right img"), {
        yPercent: -10,
        ease: "none",
        scrollTrigger: {
          trigger: section,
          start: "top bottom",
          end: "bottom top",
          scrub: true,
        },
      });
    }, section);

    return () => ctx.revert();
  }, []);

  // ── 5. Keyboard nav dans le lightbox ──────────────────────────────────
  useEffect(() => {
    if (!project) return;

    const handleKeyDown = (e) => {
      if (!lightboxOpen) return;
      if (e.key === "Escape") closeLightbox();
      if (e.key === "ArrowRight") showNextImage();
      if (e.key === "ArrowLeft") showPrevImage();
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [lightboxOpen, project, images]);

  // ── 6. Related collections ─────────────────────────────────────────────
  useEffect(() => {
    // 2 projets au hasard (différents du projet courant), tirés à chaque changement de projet.
    const keys = Object.keys(projectsCuisinesData).filter((k) => k !== path);
    for (let i = keys.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [keys[i], keys[j]] = [keys[j], keys[i]];
    }
    setRelatedCollections(keys.slice(0, 2).map((k) => [k, projectsCuisinesData[k]]));
  }, [path]);

  const openLightbox = (index) => {
    setCurrentImageIndex(index);
    setLightboxOpen(true);
    document.body.style.overflow = "hidden";
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    document.body.style.overflow = "";
  };

  const showNextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % images.gallery.length);
  };

  const showPrevImage = () => {
    setCurrentImageIndex((prev) =>
      prev === 0 ? images.gallery.length - 1 : prev - 1
    );
  };

  if (!project) return <NotFound />;

  const altBase = `Cuisine sur mesure – ${project.title}`;

  return (
    <div className="CuisinesProject">
      <section className="ProjectHero">
        <div className="ProjectHero_overlay" />
        <img
          src={images.cover}
          alt={altBase}
          fetchpriority="high"
          className="ProjectHero_img"
        />
        <div className="ProjectHero_content">
          <h1>{project.title}</h1>
        </div>
      </section>

      <section ref={sectionRef} className="ProjectBody">
        <div className="ProjectBody_left">
          <div className="ProjectImageWrap ProjectImageWrap_left">
            <img
              ref={leftImgRef}
              src={images.left}
              alt={`${altBase} – vue d'ensemble`}
              className="ProjectBody_imgLeft"
            />
          </div>
        </div>

        <div className="ProjectBody_right">
          <div className="ProjectBody_inner">
            <div className="ProjectImageWrap ProjectImageWrap_right">
              <img
                ref={rightImgRef}
                src={images.right}
                alt={`${altBase} – détail des finitions`}
                className="ProjectBody_imgTop"
              />
            </div>

            <div className="ProjectBody_Text">
              <h2>{project.title}</h2>
              <p>{project.description}</p>

              <div className="ProjectBody_Finishes">
                <h3>Finitions</h3>
                <div className="ProjectBody_FinishesList">
                  {project.finishes.map((finish, index) => (
                    <div className="FinishItem" key={index}>
                      <span className="FinishLabel">{finish.label}</span>
                      <span className="FinishName">{finish.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="ProjectGallery">
        <div className="ProjectGallery_header">
          <h2>Galerie</h2>
        </div>

        <div ref={galleryRef} className="ProjectGallery_masonry">
          {images.gallery.map((src, index) => (
            <button
              key={index}
              className="ProjectGallery_item"
              onClick={() => openLightbox(index)}
            >
              <div className="ProjectGallery_media">
                <img
                  src={src}
                  alt={`${altBase} – vue ${index + 1}`}
                  className="ProjectGallery_img"
                  loading="lazy"
                  decoding="async"
                />
              </div>
            </button>
          ))}
        </div>
      </section>

      {lightboxOpen && (
        <div className="GalleryLightbox" onClick={closeLightbox}>
          <button className="GalleryLightbox_close" onClick={closeLightbox} aria-label="Close gallery">✕</button>

          <button
            className="GalleryLightbox_nav GalleryLightbox_prev"
            onClick={(e) => { e.stopPropagation(); showPrevImage(); }}
            aria-label="Previous image"
          >‹</button>

          <div className="GalleryLightbox_content" onClick={(e) => e.stopPropagation()}>
            <span className="GalleryLightbox_counter">
              {currentImageIndex + 1} / {images.gallery.length}
            </span>
            <img
              src={images.gallery[currentImageIndex]}
              alt={`${altBase} – vue ${currentImageIndex + 1}`}
              className="GalleryLightbox_img"
            />
          </div>

          <button
            className="GalleryLightbox_nav GalleryLightbox_next"
            onClick={(e) => { e.stopPropagation(); showNextImage(); }}
            aria-label="Next image"
          >›</button>
        </div>
      )}

      <section ref={inspirationRef} className="inspiration-section">
        <div className="inspiration-left">
          <div className="inspiration-content">
            <h2>Ce projet vous inspire ?</h2>
            <p>
              Chaque espace que nous imaginons est une création exclusive,
              unique, précise et intemporelle, façonnée à l'image de chaque client.
            </p>
            <a href="/contact" target="_blank" rel="noopener noreferrer" className="inspiration-btn">
              Prendre un rendez-vous
            </a>
          </div>
        </div>

        <div className="inspiration-divider" />

        <div className="inspiration-right">
          <img src={projectImageBottom} alt="Projet d'aménagement sur mesure Elements Group" loading="lazy" decoding="async" />
        </div>
      </section>

      <section className="RelatedCollections">
        <div className="RelatedCollections_container">
          <h2 className="RelatedCollections_title">Projets similaires</h2>
          <div className="RelatedCollections_grid">
            {relatedCollections.map(([key, item], index) => (
              <Link to={`/collections/cuisines/${key}`} className="RelatedCollection_card" key={index}>
                <div className="RelatedCollection_imageWrap">
                  <img
                    src={getProjectImages("cuisines", key).cover}
                    alt={`Cuisine sur mesure – ${item.title}`}
                    className="RelatedCollection_image"
                    loading="lazy"
                    decoding="async"
                  />
                  <div className="RelatedCollection_overlay" />
                  <div className="RelatedCollection_content">
                    <span className="RelatedCollection_name">{item.title}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}